import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../../../i18n/LanguageContext";

const ProjectItem = ({
  images,
  title,
  description,
  profile,
  link,
  demoLink,
}) => {
  const { t } = useLanguage();
  const [hovered, setHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and when window resizes
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    let intervalId;
    if (hovered && images.length > 1) {
      intervalId = setInterval(() => {
        rotateImages();
      }, 1000);
    }

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered, currentImageIndex, images.length]);

  const handleHover = () => {
    setHovered(!hovered);
    setCurrentImageIndex(0);
  };

  const handleTouch = () => {
    if (isMobile) {
      setHovered(true);
      // Auto-reset hover state after 3 seconds on mobile
      setTimeout(() => {
        setHovered(false);
      }, 3000);
    }
  };

  const rotateImages = () => {
    const nextIndex = (currentImageIndex + 1) % images.length;
    setCurrentImageIndex(nextIndex);
  };

  return (
    <motion.a
      href={link}
      target="_blank"
      rel="noreferrer"
      onMouseEnter={handleHover}
      onMouseLeave={handleHover}
      onTouchStart={handleTouch}
      className="cursor-pointer bg-gradient-to-b from-[#2a2937] to-[#242331] group rounded-lg p-3 md:p-5 transition-all duration-300 border border-[rgba(67,65,85,0.4)] shadow-lg hover:shadow-xl overflow-hidden relative block h-full"
      whileHover={{ scale: isMobile ? 1 : 1.02 }}
      transition={{ type: "spring", stiffness: 300, damping: 15 }}
    >
      {/* Background glow effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/0 via-indigo-500/0 to-purple-500/0 group-hover:from-indigo-500/5 group-hover:via-indigo-500/5 group-hover:to-purple-500/5 transition-all duration-700"></div>
      
      {/* Top border gradient */}
      <div className="absolute top-0 left-0 right-0 h-[2px] scale-x-0 group-hover:scale-x-100 bg-gradient-to-r from-indigo-500/0 via-indigo-500/70 to-purple-500/0 transition-transform duration-500"></div>
      
      {/* Bottom border gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] scale-x-0 group-hover:scale-x-100 bg-gradient-to-r from-indigo-500/0 via-indigo-500/50 to-purple-500/0 transition-transform duration-500"></div>
      
      {/* Left and right border gradients */}
      <div className="absolute left-0 top-0 bottom-0 w-[2px] scale-y-0 group-hover:scale-y-100 bg-gradient-to-b from-indigo-500/0 via-indigo-500/30 to-purple-500/0 transition-transform duration-500 delay-100"></div>
      <div className="absolute right-0 top-0 bottom-0 w-[2px] scale-y-0 group-hover:scale-y-100 bg-gradient-to-b from-indigo-500/0 via-indigo-500/30 to-purple-500/0 transition-transform duration-500 delay-100"></div>
      
      <motion.div 
        className="w-full h-[200px] md:h-[309px] bg-white rounded-md overflow-hidden relative"
        whileHover={{ scale: isMobile ? 1 : 1.02 }}
        transition={{ type: "spring", stiffness: 300, damping: 10 }}
      >
        <motion.img
          key={currentImageIndex}
          className="w-full h-full object-contain"
          src={images[currentImageIndex]}
          alt=""
          initial={{ opacity: 0.8 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Image counter dots */}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
            {images.map((_, index) => (
              <div 
                key={index} 
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  index === currentImageIndex 
                    ? 'bg-indigo-500 scale-125' 
                    : 'bg-gray-400/50'
                }`}
              ></div>
            ))}
          </div>
        )}
      </motion.div>
      
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mt-4 gap-3">
        <div className="flex items-center gap-3 md:gap-4">
          <motion.div 
            className="w-8 h-8 md:w-10 md:h-10 rounded-full overflow-hidden border-2 border-transparent group-hover:border-indigo-500 transition-all duration-300"
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ type: "spring", stiffness: 300, damping: 10 }}
          >
            <img className="rounded-full w-full h-full object-cover" src={profile} alt="" />
          </motion.div>
          <div>
            <motion.h3 
              className="text-white font-inter font-semibold text-sm md:text-base group-hover:text-indigo-200 transition-colors duration-300"
              initial={{ y: 0 }}
              whileHover={{ y: -2 }}
              transition={{ type: "spring", stiffness: 300, damping: 10 }}
            >
              {title}
            </motion.h3>
            <motion.p 
              className="text-indigo-300/70 font-inter font-medium text-xs md:text-sm group-hover:text-indigo-300 transition-all duration-300"
              initial={{ y: 0 }}
              whileHover={{ y: -2 }}
              transition={{ type: "spring", stiffness: 300, damping: 10 }}
            >
              {description}
            </motion.p>
          </div>
        </div>
        {demoLink && (
          <motion.a
            target="_blank"
            rel="noreferrer"
            href={demoLink}
            onClick={(e) => e.stopPropagation()}
            className="group/button flex items-center justify-center md:justify-start gap-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-inter text-xs md:text-sm px-3 py-2 rounded-lg transition-all duration-300 hover:shadow-md hover:shadow-indigo-500/20"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 10 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              className="fill-white group-hover/button:translate-x-0.5 transition-transform duration-300"
            >
              <path
                d="M40.96 4.98a2 2 0 00-.22.02H28a2 2 0 100 4h8.172L22.586 22.586a2 2 0 102.828 2.828L39 11.828V20a2 2 0 104 0V7.246a2 2 0 00-2.04-2.266zM12.5 8C8.383 8 5 11.383 5 15.5v20c0 4.117 3.383 7.5 7.5 7.5h20c4.117 0 7.5-3.383 7.5-7.5V26a2 2 0 10-4 0v9.5c0 1.947-1.553 3.5-3.5 3.5h-20A3.483 3.483 0 019 35.5v-20c0-1.947 1.553-3.5 3.5-3.5H22a2 2 0 100-4h-9.5z"
                transform="matrix(.53 0 0 .53 12 12) translate(-24.01 -23.99)"
              ></path>
            </svg>
            <span className="relative">
              {t.github.viewDemo}
              <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-white group-hover/button:w-full transition-all duration-300"></span>
            </span>
          </motion.a>
        )}
      </div>
    </motion.a>
  );
};

export default ProjectItem;
